import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AppRole = 'admin' | 'koordynator' | 'serwisant'

export interface InstallerUser {
  id: string
  name: string
  email: string
  company: string
  companyId: string
  role: AppRole
  permissions: string[] // efektywne uprawnienia (rola + nadpisania); admin = pełna lista
  avatar?: string
}

export interface LoginResult {
  ok: boolean
  requiresDeviceConfirmation?: boolean
  requiresTotp?: boolean
  challengeId?: string
  emailHint?: string
  error?: string
}

interface AuthState {
  token: string | null
  user: InstallerUser | null
  isAuthenticated: boolean
  theme: 'dark' | 'light'
  login: (email: string, password: string) => Promise<LoginResult>
  /** Potwierdzenie nowego urządzenia kodem z e-maila (nie-admini). */
  confirmDevice: (challengeId: string, code: string, remember: boolean) => Promise<{ ok: boolean; error?: string }>
  /** Potwierdzenie 2FA (TOTP) przy logowaniu admina. */
  confirmTotp: (challengeId: string, code: string) => Promise<{ ok: boolean; error?: string }>
  logout: () => void
  toggleTheme: () => void
  checkAuth: () => Promise<boolean>
  /** Czy bieżący user ma dane uprawnienie (admin ma wszystko). */
  can: (key: string) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      isAuthenticated: false,
      theme: 'light',

      login: async (email: string, password: string): Promise<LoginResult> => {
        try {
          const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          })
          const data = await res.json().catch(() => ({}))

          // Nowe urządzenie (nie-admin) → potwierdzenie kodem z e-maila.
          if (res.ok && data.requiresDeviceConfirmation) {
            return { ok: false, requiresDeviceConfirmation: true, challengeId: data.challengeId, emailHint: data.emailHint }
          }
          // Admin z 2FA → kod z authenticatora.
          if (res.ok && data.requiresTotp) {
            return { ok: false, requiresTotp: true, challengeId: data.challengeId }
          }
          if (!res.ok) return { ok: false, error: data.error || 'Ungültige Anmeldedaten' }

          set({
            token: data.token,
            user: {
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              company: data.user.company,
              companyId: data.user.companyId,
              role: data.user.role as AppRole,
              permissions: data.user.permissions ?? [],
            },
            isAuthenticated: true,
          })
          return { ok: true }
        } catch {
          return { ok: false, error: 'Verbindungsfehler' }
        }
      },

      confirmDevice: async (challengeId: string, code: string, remember: boolean) => {
        try {
          const res = await fetch('/api/auth/confirm-device', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challengeId, code, remember }),
          })
          const data = await res.json().catch(() => ({}))
          if (!res.ok) return { ok: false, error: data.error || 'Falscher Code' }

          set({
            token: data.token,
            user: {
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              company: data.user.company,
              companyId: data.user.companyId,
              role: data.user.role as AppRole,
              permissions: data.user.permissions ?? [],
            },
            isAuthenticated: true,
          })
          return { ok: true }
        } catch {
          return { ok: false, error: 'Verbindungsfehler' }
        }
      },

      confirmTotp: async (challengeId: string, code: string) => {
        try {
          const res = await fetch('/api/auth/confirm-totp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challengeId, code }),
          })
          const data = await res.json().catch(() => ({}))
          if (!res.ok) return { ok: false, error: data.error || 'Falscher Code' }

          set({
            token: data.token,
            user: {
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              company: data.user.company,
              companyId: data.user.companyId,
              role: data.user.role as AppRole,
              permissions: data.user.permissions ?? [],
            },
            isAuthenticated: true,
          })
          return { ok: true }
        } catch {
          return { ok: false, error: 'Verbindungsfehler' }
        }
      },

      logout: () => {
        const token = get().token
        if (token) {
          fetch('/api/auth/logout', {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {})
        }
        set({
          token: null,
          user: null,
          isAuthenticated: false,
        })
      },

      checkAuth: async () => {
        const token = get().token
        if (!token) return false

        try {
          const res = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` },
          })
          if (!res.ok) {
            set({ token: null, user: null, isAuthenticated: false })
            return false
          }
          const data = await res.json()
          // Konto dezaktywowane w trakcie sesji — wypinamy natychmiast.
          if (data.user.active === false) {
            set({ token: null, user: null, isAuthenticated: false })
            return false
          }
          set({
            user: {
              id: data.user.id,
              name: data.user.name,
              email: data.user.email,
              company: data.user.company,
              companyId: data.user.companyId,
              role: data.user.role as AppRole,
              permissions: data.user.permissions ?? [],
            },
            isAuthenticated: true,
          })
          return true
        } catch {
          // Chwilowy brak sieci NIE może wylogowywać — inaczej każdy tunel
          // czy zerwane wifi wyrzucałoby montera z apki w środku roboty.
          // Sesję kasuje wyłącznie jednoznaczna odmowa serwera (powyżej).
          return false
        }
      },

      toggleTheme: () => {
        const newTheme = get().theme === 'dark' ? 'light' : 'dark'
        set({ theme: newTheme })
      },

      can: (key: string) => {
        const u = get().user
        if (!u) return false
        if (u.role === 'admin') return true
        return (u.permissions || []).includes(key)
      },
    }),
    {
      name: 'heizos-auth',
    }
  )
)
